import type { Command, CommandInput } from "@vortex-os/slash-commands";
import { WorklogStore, appendSection } from "@vortex-os/worklog";
import { ensureWorklogEntry } from "../worklog-write.js";

export interface WorklogAppendResult {
  readonly path: string;
  readonly date: string;
  readonly keyword: string;
  readonly sectionTitle: string;
  /** True when this call had to create today's worklog file first. */
  readonly createdEntry: boolean;
}

/**
 * `/log <section-title>` — Append a `## <section-title>` section to today's
 * worklog entry, creating the entry first when none exists yet (via
 * `ensureWorklogEntry`, the same create primitive the auto-worklog paths use —
 * so the documented "creates it if needed" contract holds on a fresh day).
 * The body of the new section is left empty for the caller to fill in.
 *
 * This is a deliberately small command — the goal is to make "add a quick
 * note to today's worklog" a one-liner, not to replace a real editor.
 */
export const logCommand: Command<WorklogAppendResult> = {
  name: "log",
  description:
    "Append a `## <section-title>` section to today's worklog entry (creates the entry first if none exists yet today).",
  args: [
    {
      name: "section",
      description: "Title for the new `## ` section (rest of the input).",
      required: true,
    },
  ],
  handler: async (input: CommandInput): Promise<WorklogAppendResult> => {
    const sectionTitle = input.rest.trim();
    if (!sectionTitle) {
      throw new Error("`/log` requires a section title.");
    }
    // A help flag (or any bare `-…`/`--…` token) is NOT a section title — earlier
    // it was taken literally and created a junk `## --help` entry. Reject it with
    // usage and write NOTHING, so `vortex log --help` behaves like a help request
    // instead of silently polluting today's worklog.
    if (/^(--help|-h|-\?|help)$/i.test(sectionTitle) || /^-{1,2}[^\s]*$/.test(sectionTitle)) {
      throw new Error(
        "Usage: vortex log <section-title> — appends a `## <section-title>` section to today's " +
          "worklog (creating today's entry if needed). A flag like `--help` is not a section title, " +
          "so nothing was written.",
      );
    }

    // When this has to CREATE today's worklog, name it after the section topic — so a
    // new day's file is `…_HHMM-<topic>.md`, never the generic `…-worklog.md` (which
    // broke the naming convention; recurred 2026-06). The keyword is ignored when an
    // entry already exists for the day. ensureWorklogEntry slugifies + guards it.
    const ensured = await ensureWorklogEntry(input.context, { keyword: sectionTitle });
    // Re-read through the store for the full WorklogEntry shape appendSection
    // takes (frontmatter + body) — ensureWorklogEntry returns only the pointer.
    const store = new WorklogStore(`${input.context.dataDir}/worklog`);
    const todayEntry = await store.get(ensured.date);
    if (!todayEntry) {
      // ensureWorklogEntry just wrote it — only a racing delete gets here.
      throw new Error(`Could not read back today's worklog at ${ensured.path}.`);
    }

    await appendSection(todayEntry, sectionTitle, "");
    return {
      path: todayEntry.path,
      date: todayEntry.date,
      keyword: todayEntry.keyword,
      sectionTitle,
      createdEntry: ensured.created,
    };
  },
};
